import React, { useCallback, useEffect, useState } from 'react';

import Masonry from '@repo/editor/src/components/Masonry/Masonry';
import { ColumnItem } from '@repo/editor/src/components/Masonry/types';
import {
  authenticationStoreSelector,
  useAuthenticationStore,
} from '@repo/editor/src/stores/authenticationStore';
import {
  designsStoreSelector,
  useDesignsStore,
} from '@repo/editor/src/stores/designsStore';
import {
  promotionSelector,
  usePromotionStore,
} from '@repo/editor/src/stores/promotionStore';
import {
  userStoreSelector,
  useUserStore,
} from '@repo/editor/src/stores/userStore';
import { Post } from '@repo/editor/src/types';
import { secondaryTheme } from '@repo/ui/components/Buttons';
import { buildEditorUrl } from '@repo/utils/url';

import LinkButton from 'components/LinkButton/LinkButton';
import NewProjectTile from 'components/NewProjectTile/NewProjectTile';
import NoPostsLoaded from 'components/NoPostsLoaded/NoPostsLoaded';
import PostPreview from 'components/PostPreview/PostPreview';
import postApi from 'global/postApi';
import {
  buildNewDesignUrl,
  buildEditorUrlWithTemplateParams,
} from 'global/urlUtils';
import { useActiveWorkspaceId } from 'global/utils/workspace/useActiveWorkspaceId';
import { postsStoreSelector, usePostsStore } from 'stores/postsStore';
import { FlexRow } from 'styles/layouts';

import { FeedWrapperProps } from './types';

export const columnGap = 24;
const postsLength = 15;

const renderPreview = (
  post: ColumnItem<Post | { id: 'new' }>,
  handleClick: (post: Post | { id: 'new' }) => void,
  handlePostDetailsClick: (post: Post | { id: 'new' }) => void,
  userId: string | null
): JSX.Element => {
  if (post.id === 'new') {
    return <NewProjectTile onClick={() => handleClick({ id: 'new' })} />;
  }
  return (
    <PostPreview
      key={userId} // we need to update the post preview if the user changed
      areLikesAndPopularityHiddenOnMobile
      canLike
      clickAction="openEditor"
      post={post as ColumnItem<Post>}
      onClick={handleClick}
      onPostDetailsClick={handlePostDetailsClick}
    />
  );
};

export const FeedWrapper: React.FC<FeedWrapperProps> = ({
  isMobile,
  columnWidth,
  clickedPost,
  setClickedPost,
  handlePostDetailsClick,
  templates,
  moreButtonLink,
}) => {
  const user = useUserStore(userStoreSelector.user);
  const proAccess = useUserStore(userStoreSelector.proAccess);
  const workspaceId = useActiveWorkspaceId();
  const openAuthentication = useAuthenticationStore(
    authenticationStoreSelector.openAuthentication
  );
  const showUpgradeModal = usePromotionStore(
    promotionSelector.showUpgradeModal
  );
  const canUserCreateDesign = useDesignsStore(
    designsStoreSelector.canUserCreateDesign
  );

  const getPost = usePostsStore(postsStoreSelector.getPost);
  const addPost = usePostsStore(postsStoreSelector.addPost);

  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState<(Post | { id: 'new' })[]>([]);

  const checkIfEditingAvailable = useCallback(
    (item: Post | { id: 'new' }): boolean => {
      const isNewId = item.id === 'new';

      if (isNewId) {
        if (user?.designsLimitReached) {
          const type =
            user?.plan === 'PRO'
              ? 'proProjectLimitReached'
              : 'projectLimitReached';
          showUpgradeModal(type);
          return false;
        }
        return true;
      }

      const post = item as Post;
      if (post.template?.premium && !proAccess) {
        showUpgradeModal('premiumDesignUsage', {
          designPreview: post.template.templatePreviewObjectName,
          data: {
            templateId: post.template.id,
          },
        });
        return false;
      }

      return true;
    },
    [proAccess, showUpgradeModal, user?.designsLimitReached, user?.plan]
  );

  useEffect(() => {
    if (!user?.id || !clickedPost) {
      return;
    }

    if (!checkIfEditingAvailable(clickedPost)) {
      setClickedPost(null);
      return;
    }

    const templateId =
      clickedPost.id === 'new'
        ? undefined
        : (clickedPost as Post)?.template?.id;

    if (clickedPost.id === 'new' || templateId === undefined) {
      window.open(buildEditorUrl(), '_self');
    } else {
      const url = buildEditorUrlWithTemplateParams({ id: templateId });

      canUserCreateDesign({
        canShowProjectListModal: true,
        redirectUrl: url,
        cb: () => window.open(url, '_blank'),
      });
    }
  }, [
    clickedPost,
    checkIfEditingAvailable,
    user?.id,
    setClickedPost,
    canUserCreateDesign,
  ]);

  const handleClickPost = useCallback(
    async (post: Post | { id: 'new' }): Promise<void> => {
      if (!user?.id) {
        openAuthentication({
          onSubmit: () => setClickedPost(post),
        });
        return;
      }

      const isEditingAvailable = checkIfEditingAvailable(post);
      if (
        (await canUserCreateDesign()) &&
        post.id === 'new' &&
        isEditingAvailable
      ) {
        window.open(buildNewDesignUrl({ workspaceId }), '_self');
      }
    },
    [
      checkIfEditingAvailable,
      openAuthentication,
      setClickedPost,
      user?.id,
      canUserCreateDesign,
      workspaceId,
    ]
  );

  useEffect(() => {
    postApi
      .filter({
        take: postsLength,
        ids: templates,
        sort: 'popularity',
      })
      .then((response) => {
        if (!response?.results?.length) {
          setLoading(false);
          return;
        }

        response.results.forEach((post) => {
          if (!getPost(post.id)) {
            addPost(post);
          }
        });
        if (isMobile) {
          setPosts(response.results);
        } else {
          setPosts([{ id: 'new' }, ...response.results]);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [addPost, getPost, isMobile, templates]);

  return (
    <>
      <Masonry<Post | { id: 'new' }>
        gap={columnGap}
        items={posts}
        minColumnWidth={columnWidth}
        render={(item): JSX.Element =>
          renderPreview(
            item,
            handleClickPost,
            handlePostDetailsClick,
            user?.id ?? null
          )
        }
      />

      {!posts.length && <NoPostsLoaded loading={loading} />}
      {!!posts.length && (
        <FlexRow justifyContent="center">
          <LinkButton
            height="40px"
            href={moreButtonLink}
            label="Browse More Templates"
            loading={loading}
            theme={secondaryTheme}
            width="auto"
          />
        </FlexRow>
      )}
    </>
  );
};
